import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { tap } from 'rxjs/operators';
import { EmployerDto, LocationV2Dto, LocationRestService, LocationGeometryDto } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private employerDto: EmployerDto = null;

  private locations: LocationV2Dto[] = [];
  private locations$ = new BehaviorSubject<LocationV2Dto[]>([]);
  private locationsWD: LocationV2Dto[] = [];
  private locationsWD$ = new BehaviorSubject<LocationV2Dto[]>([]);

  constructor(
    private access: AuthService,
    private employerService: EmployerService,
    private locationRest: LocationRestService,
    private errorHandler: ErrorHandlerService,
    private log: NGXLogger,
    private alertService: AlertService,
    private translate: TranslateService
  ) {
    this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;

      if (this.employerDto) {
        this.reloadLocations();
      } else {
        this.locationsWD = [];
        this.locations = [];
        this.locationsWD$.next(this.locationsWD);
        this.locations$.next(this.locations);
      }
    });
    this.log.debug('Construct');
  }

  reloadLocations(): Observable<Boolean> {
    let retListener: Subject<Boolean> = new Subject<Boolean>();

    this.locationsWD = [];
    this.locations = [];
    if (this.employerDto && this.access.hasGrantOwner(ExaGrant.READ_LOCATION, this.employerDto.id)) {
      this.locationRest.getLocationsUsingPOST(this.employerDto.username, { size: 200 }).subscribe(
        (locations) => {
          this.locationsWD = locations.content;
          this.locationsWD$.next(this.locationsWD);
          this.locations = this.filterDeletedElements(this.locationsWD);
          this.locations$.next(this.locations);

          retListener.next(true);
        },
        (err) => {
          this.locationsWD = [];
          this.locationsWD$.next(this.locationsWD);
          this.locations = [];
          this.locations$.next(this.locations);
          this.errorHandler.handleHttpError(err);

          retListener.next(false);
        }
      );
    } else {
      this.locations$.next(this.locations);
      this.locationsWD$.next(this.locationsWD);

      retListener.next(false);
    }

    return retListener;
  }

  private filterDeletedElements(elementsWithDeleted: LocationV2Dto[]): LocationV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  private filterNonDeletedElements(elementsWithDeleted: LocationV2Dto[]): LocationV2Dto[] {
    return elementsWithDeleted.filter((elem) => elem.deleted);
  }

  onGetLocations(withDeleted?: boolean): Observable<LocationV2Dto[]> {
    if (withDeleted) {
      return this.locationsWD$;
    }
    return this.locations$;
  }

  getLocationsInactive(): LocationV2Dto[] {
    return this.filterNonDeletedElements(this.locationsWD);
  }

  getLocationById(locationId: number): LocationV2Dto {
    if (locationId) {
      for (let location of this.locationsWD) {
        if (location.id == locationId) {
          return location;
        }
      }
    }
    return null;
  }

  getLocationsByCustomerId(customerId: number): LocationV2Dto[] {
    const locations: LocationV2Dto[] = [];
    if (customerId) {
      for (let location of this.locationsWD) {
        if (location.customer.id == customerId) {
          locations.push(location);
        }
      }
    }
    return locations;
  }

  // bisherige add Location methode
  // addLocation(locationDto: LocationDto): Observable<LocationDto> {
  //   // Hook the post call and refresh the fields
  //   return this.locationRest.addLocationUsingPOST(locationDto.ownerUsername, locationDto).pipe(tap(ret => this.reloadLocations()));
  // }

  addLocation(locationDto: LocationV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.locationRest.addLocationUsingPOST(locationDto.owner.username, locationDto).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('LOCATION.ADD.SUCCESS'));
          console.log(' Location hinzugefügt.');
          this.reloadLocations();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  // bisherige Update Location methode
  // updateLocation(locationDto: LocationDto): Observable<LocationDto> {
  //   return this.locationRest.updateLocationUsingPOST(locationDto.id, locationDto).pipe(tap(ret => this.reloadLocations()));
  // }
  updateLocation(locationDto: LocationV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.locationRest.updateLocationUsingPUT(locationDto.id, locationDto).subscribe(
        (ret) => {
          this.alertService.openMessageToast(this.translate.instant('LOCATION.EDIT.SUCCESS'));
          console.log(' location edit erfolgreich');
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  updateItemGeometry(item: LocationV2Dto): Promise<LocationV2Dto> {
    return firstValueFrom(this.locationRest.updateLocationGeometryUsingPUT(item.id!, item.geometry!));
  }

  updateLocationGeometry(location: LocationV2Dto, geometry: LocationGeometryDto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.locationRest.updateLocationGeometryUsingPUT(location.id, geometry).subscribe(
        (ret) => {
          this.alertService.openMessageToast(this.translate.instant('LOCATION.EDIT.SUCCESS'));
          console.log(' location edit erfolgreich');
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteLocation(locationId: number): Promise<void> {
    // bisher: deleteLocation(locationId: number): Observable <null> {
    //this.locationRest.deleteLocationUsingDELETE(locationId).pipe(tap(ret => this.reloadLocations()));
    // }

    return new Promise<void>((resolve, reject) => {
      this.locationRest.deleteLocationUsingDELETE(locationId).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('LOCATION.DELETE.SUCCESS'));
          console.log(' LocationRest.deleteMLocationUsingDELETE -> SUCCESS');
          this.reloadLocations();
          resolve();

          // error
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' locationRest.deleteLocationUsingDELETE -> ERROR');
          reject(err);
        }
      );
    });
  }

  // reactivateLocation(id: number): Observable<null> {
  //   return this.locationRest.reactivateLocationUsingPOST(id).pipe(tap(ret => this.reloadLocations()));
  // }

  hasLongOfflineLocation(): boolean {
    var dateOffset = 24 * 60 * 60 * 1000 * 4; //4 days
    let deadline = new Date();
    deadline.setTime(deadline.getTime() - dateOffset);
    for (const location of this.locations) {
      if (location.lastOnline && Number(location.lastOnline) <= deadline.getTime()) {
        this.log.debug(deadline);
        this.log.debug(location);
        return true;
      }
    }
    return false;
  }

  isLongOffline(location: LocationV2Dto) {
    var dateOffset = 24 * 60 * 60 * 1000 * 4; //4 days
    let deadline = new Date();
    deadline.setTime(deadline.getTime() - dateOffset);
    return location.lastOnline && Number(location.lastOnline) <= deadline.getTime();
  }
}
